import type { ReactNode } from 'react';
import type { District } from '@neopolis/engine';

import { T } from '../textes-interface.js';
import { coins, losange, proj, pts } from './carte/iso.js';
import {
  Arbre,
  Benne,
  Bloc,
  Buisson,
  Capteur,
  Champ,
  Ecole,
  Etang,
  Gare,
  Gymnase,
  Hangar,
  Immeuble,
  Lampadaire,
  Mairie,
  Maison,
  Voiture,
  VoieFerree,
} from './carte/volumes.js';

/**
 * Plan d'un quartier, en coordonnées de la grille isométrique.
 *
 * Les identifiants et les noms viennent du contenu ; la géométrie et le décor
 * sont du dessin, et vivent donc ici. `profondeur` fixe l'ordre de tracé : en
 * vue de trois quarts, un quartier situé devant doit être peint après celui
 * qu'il recouvre, sinon les façades se traversent.
 */
interface PlanDeQuartier {
  readonly parcelle: {
    readonly gx: number;
    readonly gy: number;
    readonly l: number;
    readonly p: number;
  };
  readonly profondeur: number;
  readonly sol: string;
  readonly solEquipe: string;
  readonly etiquette: { readonly gx: number; readonly gy: number; readonly h: number };
  readonly lampes: readonly (readonly [number, number])[];
  readonly decor: (equipe: boolean) => ReactNode;
}

const PLANS: Record<string, PlanDeQuartier> = {
  tilleuls: {
    parcelle: { gx: 1, gy: 1, l: 7, p: 7 },
    profondeur: 1,
    sol: '#D9F2CE',
    solEquipe: '#BEEBAC',
    etiquette: { gx: 4.5, gy: 9.4, h: 16 },
    lampes: [
      [1.2, 7.9],
      [7.4, 7.9],
      [7.5, 4.6],
    ],
    decor: (equipe) => (
      <>
        <Capteur gx={7.7} gy={1.2} actif={equipe} />
        <Ecole gx={1.3} gy={1.3} allumee={equipe} />
        <Gymnase gx={4.7} gy={1.3} />
        <Arbre gx={0.5} gy={3.2} taille={0.9} />
        {/* La cour de récréation, derrière l'école. */}
        <polygon points={losange(4.9, 3.5, 2.2, 2.0)} fill="#FCD9A0" opacity="0.92" />
        <ellipse
          cx={proj(6.0, 4.5).x}
          cy={proj(6.0, 4.5).y}
          rx="18"
          ry="9"
          fill="none"
          stroke="#E8913A"
          strokeWidth="2"
        />
        <Arbre gx={7.6} gy={3.4} taille={0.8} teinte="#65A30D" />
        <Maison gx={1.3} gy={4.0} teinte="#FBD0E0" toit="#9F1239" allumee={equipe} />
        <Maison gx={1.3} gy={5.9} teinte="#C7DEFB" toit="#1E40AF" allumee={equipe} />
        <Maison gx={3.7} gy={5.9} teinte="#FDE9A9" toit="#B45309" allumee={equipe} />
        <Buisson gx={0.6} gy={6.6} />
        <Arbre gx={6.6} gy={6.2} taille={0.75} />
        <Buisson gx={7.5} gy={7.1} />
      </>
    ),
  },

  ateliers: {
    parcelle: { gx: 10, gy: 1, l: 7, p: 7 },
    profondeur: 2,
    sol: '#E6E3DE',
    solEquipe: '#D4D0C9',
    etiquette: { gx: 13.5, gy: 9.4, h: 16 },
    lampes: [
      [10.3, 7.9],
      [16.5, 7.9],
      [16.8, 3.3],
    ],
    decor: (equipe) => (
      <>
        <Capteur gx={16.8} gy={1.2} actif={equipe} />
        <Hangar gx={10.4} gy={1.4} />
        <Hangar gx={13.7} gy={1.4} teinte="#DCDAD6" />
        <Hangar gx={10.4} gy={3.6} teinte="#DCDAD6" />
        <Hangar gx={13.7} gy={3.6} />
        <Arbre gx={16.9} gy={5.4} taille={0.75} teinte="#4D7C0F" />
        {/* Le centre de tri et ses quatre flux. */}
        <polygon points={losange(10.5, 6.0, 3.6, 1.3)} fill="#CFCCC6" />
        <Benne gx={10.8} gy={6.3} couvercle="#15803D" />
        <Benne gx={11.7} gy={6.3} couvercle="#CA8A04" />
        <Benne gx={12.6} gy={6.3} couvercle="#1D4ED8" />
        <Benne gx={13.5} gy={6.3} couvercle="#57534E" />
        <Voiture gx={15.4} gy={6.4} teinte="#DC2626" />
        <Buisson gx={16.8} gy={7.0} />
      </>
    ),
  },

  rives: {
    parcelle: { gx: 1, gy: 10, l: 5.5, p: 7 },
    profondeur: 3,
    sol: '#CDEECB',
    solEquipe: '#B2E6AF',
    etiquette: { gx: 3.75, gy: 18.6, h: 16 },
    lampes: [
      [1.2, 16.9],
      [5.9, 16.9],
      [6.1, 13.2],
    ],
    decor: (equipe) => (
      <>
        <Capteur gx={6.2} gy={10.4} actif={equipe} />
        {/* Les allées du parc, tracées en clair sur la pelouse. */}
        <path
          d={`M ${proj(1.2, 10.3).x} ${proj(1.2, 10.3).y}
              Q ${proj(4.6, 11.9).x} ${proj(4.6, 11.9).y} ${proj(2.6, 14.2).x} ${proj(2.6, 14.2).y}
              Q ${proj(1.3, 15.9).x} ${proj(1.3, 15.9).y} ${proj(5.5, 16.8).x} ${proj(5.5, 16.8).y}`}
          fill="none"
          stroke="#EADFAE"
          strokeWidth="10"
          strokeLinecap="round"
        />
        <Arbre gx={1.5} gy={10.5} taille={1} />
        <Arbre gx={3.7} gy={10.4} taille={0.85} teinte="#15803D" />
        <Arbre gx={5.4} gy={11.3} taille={0.95} />
        <Etang gx={2.5} gy={12.2} l={2.4} p={2.1} />
        <Buisson gx={1.3} gy={13.0} />
        <Buisson gx={5.7} gy={13.8} />
        <Arbre gx={1.4} gy={14.9} taille={1.05} teinte="#166534" />
        <Arbre gx={5.0} gy={14.7} taille={0.9} />
        <Arbre gx={2.9} gy={16.3} taille={0.8} teinte="#65A30D" />
        <Buisson gx={4.4} gy={16.6} />
      </>
    ),
  },

  centre: {
    parcelle: { gx: 8, gy: 10, l: 5.6, p: 7 },
    profondeur: 4,
    sol: '#F6E7C6',
    solEquipe: '#EFD9A6',
    etiquette: { gx: 10.8, gy: 18.6, h: 16 },
    lampes: [
      [8.2, 16.9],
      [13.4, 16.9],
      [13.4, 12.9],
    ],
    decor: (equipe) => (
      <>
        <Capteur gx={13.3} gy={10.3} actif={equipe} />
        <Mairie gx={8.4} gy={10.4} allumee={equipe} />
        <Arbre gx={13.0} gy={12.3} taille={0.75} teinte="#4D7C0F" />
        <Immeuble gx={8.2} gy={13.2} h={78} teinte="#E8B9C8" allume={equipe} />
        <Immeuble gx={10.1} gy={13.2} h={58} teinte="#AFC6E4" allume={equipe} />
        <Immeuble gx={11.9} gy={13.2} h={68} teinte="#C2B4E6" allume={equipe} />
        <Immeuble gx={8.2} gy={15.0} h={62} teinte="#E3CBA6" allume={equipe} />
        <Immeuble gx={10.1} gy={15.0} h={86} teinte="#9FCFC4" allume={equipe} />
        <Buisson gx={13.1} gy={15.2} />
        <Voiture gx={12.4} gy={16.4} teinte="#0EA5E9" />
      </>
    ),
  },

  gare: {
    parcelle: { gx: 14.5, gy: 10, l: 5.5, p: 7 },
    profondeur: 5,
    sol: '#DCDCF2',
    solEquipe: '#C9C9EC',
    etiquette: { gx: 17.25, gy: 18.6, h: 16 },
    lampes: [
      [14.7, 16.9],
      [19.8, 16.9],
      [19.9, 14.6],
    ],
    decor: (equipe) => (
      <>
        <Capteur gx={19.7} gy={10.3} actif={equipe} />
        <VoieFerree gx={14.6} gy={10.25} l={5.3} />
        <Gare gx={15.0} gy={11.4} allumee={equipe} />
        {/* Le parking du parvis et ses places. */}
        <polygon points={losange(14.8, 14.9, 4.6, 1.9)} fill="#C6C6D8" />
        {[0.7, 1.6, 2.5, 3.4].map((t) => (
          <line
            key={t}
            x1={proj(14.8 + t, 14.95).x}
            y1={proj(14.8 + t, 14.95).y}
            x2={proj(14.8 + t, 16.75).x}
            y2={proj(14.8 + t, 16.75).y}
            stroke="#F1F1F7"
            strokeWidth="2"
          />
        ))}
        <Voiture gx={15.2} gy={15.3} teinte="#DC2626" />
        <Voiture gx={16.1} gy={15.3} teinte="#2563EB" />
        <Voiture gx={17.0} gy={15.3} teinte="#16A34A" />
        <Voiture gx={17.9} gy={15.3} teinte="#F59E0B" />
        <Buisson gx={19.8} gy={13.6} />
        <Buisson gx={14.4} gy={13.4} />
      </>
    ),
  },
};

/** Le socle de la ville : une dalle de terrain avec son épaisseur. */
function Socle() {
  const c = coins(-0.4, -0.4, 21.2, 18.6);
  const bas = coins(-0.4, -0.4, 21.2, 18.6, -20);
  return (
    <g aria-hidden="true">
      <polygon points={pts(c.o, c.s, bas.s, bas.o)} fill="#9AB87E" />
      <polygon points={pts(c.s, c.e, bas.e, bas.s)} fill="#7E9A66" />
      <polygon points={pts(c.n, c.e, c.s, c.o)} fill="#C7E3A6" />
    </g>
  );
}

/** Une chaussée, posée à plat entre deux parcelles. */
function Route({
  gx,
  gy,
  l,
  p,
  sens,
}: {
  readonly gx: number;
  readonly gy: number;
  readonly l: number;
  readonly p: number;
  readonly sens: 'gx' | 'gy';
}) {
  const depart = sens === 'gx' ? proj(gx, gy + p / 2) : proj(gx + l / 2, gy);
  const arrivee = sens === 'gx' ? proj(gx + l, gy + p / 2) : proj(gx + l / 2, gy + p);
  return (
    <g aria-hidden="true">
      <polygon points={losange(gx, gy, l, p)} fill="#D5D7DC" />
      <line
        x1={depart.x}
        y1={depart.y}
        x2={arrivee.x}
        y2={arrivee.y}
        stroke="#FAFAFA"
        strokeWidth="2.5"
        strokeDasharray="9 8"
        opacity="0.9"
      />
    </g>
  );
}

/**
 * La carte de Néopolis, en vue isométrique.
 *
 * Chaque quartier est un bouton : il s'active au doigt, à la souris, et au
 * clavier (Entrée ou Espace). Un quartier équipé verdit son sol, allume ses
 * fenêtres et ses lampadaires, et met ses capteurs à émettre — c'est la
 * récompense visible du travail de l'îlot.
 */
export function CarteNeopolis({
  quartiers,
  quartiersEquipes,
  quartierActif,
  onChoisir,
}: {
  quartiers: readonly District[];
  quartiersEquipes: readonly string[];
  quartierActif: string | null;
  onChoisir: (quartierId: string) => void;
}) {
  const dessinables = quartiers
    .flatMap((quartier) => {
      const plan = PLANS[quartier.id];
      return plan ? [{ quartier, plan }] : [];
    })
    .sort((a, b) => a.plan.profondeur - b.plan.profondeur);

  return (
    <figure className="m-0">
      <svg
        viewBox="0 0 940 590"
        className="h-auto w-full rounded-2xl"
        role="group"
        aria-label={T.carte.titre}
      >
        <defs>
          <linearGradient id="ciel-neopolis" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#CFEBFB" />
            <stop offset="100%" stopColor="#EDF6FD" />
          </linearGradient>
        </defs>

        <rect width="940" height="590" rx="16" fill="url(#ciel-neopolis)" />

        <Socle />

        {/*
          La campagne qui entoure la ville. Sans elle, les quatre pointes du
          socle restent des aplats verts vides et la ville a l'air posée sur
          une table.
        */}
        <g aria-hidden="true">
          <Champ gx={17.5} gy={0.4} l={2.9} p={2.4} />
          <Champ gx={17.5} gy={3.2} l={2.9} p={2.1} teinte="#D7CE93" />
          <Arbre gx={20.2} gy={0.9} taille={0.85} teinte="#166534" />
          <Arbre gx={17.4} gy={6.2} taille={0.9} />
          <Arbre gx={19.9} gy={6.6} taille={0.75} teinte="#4D7C0F" />
          <Champ gx={17.4} gy={5.6} l={3.1} p={2.2} teinte="#CBDF9A" />
          <Arbre gx={20.6} gy={4.4} taille={0.85} teinte="#166534" />
          <Arbre gx={20.5} gy={7.4} taille={0.8} />
          <Arbre gx={20.4} gy={10.6} taille={0.9} teinte="#4D7C0F" />
          <Arbre gx={20.5} gy={13.8} taille={0.75} />
          <Arbre gx={20.4} gy={16.4} taille={0.85} teinte="#166534" />
          <Arbre gx={2.4} gy={0.2} taille={0.8} teinte="#15803D" />
          <Buisson gx={5.6} gy={0.2} />
          <Arbre gx={0.2} gy={2.2} taille={0.9} />
          <Arbre gx={0.4} gy={5.4} taille={0.75} teinte="#4D7C0F" />
          <Buisson gx={0.2} gy={8.8} />
          <Arbre gx={0.3} gy={11.6} taille={0.95} teinte="#166534" />
          <Arbre gx={0.2} gy={14.8} taille={0.8} />
        </g>

        {/* La voirie passe dans les saignées entre les parcelles. */}
        <Route gx={8} gy={-0.4} l={2} p={18.6} sens="gy" />
        <Route gx={-0.4} gy={8} l={21.2} p={2} sens="gx" />
        <Route gx={6.5} gy={10} l={1.5} p={8.2} sens="gy" />
        <Route gx={13.6} gy={10} l={0.9} p={8.2} sens="gy" />

        {/* Quelques véhicules sur les axes, pour que la ville ne soit pas déserte. */}
        <g aria-hidden="true">
          <Voiture gx={8.6} gy={2.4} teinte="#F59E0B" />
          <Voiture gx={8.6} gy={5.8} teinte="#8B5CF6" />
          <Voiture gx={9.2} gy={4.1} teinte="#FAFAFA" />
          <Voiture gx={3.4} gy={8.6} teinte="#DC2626" />
          <Voiture gx={11.8} gy={8.6} teinte="#0EA5E9" />
          <Voiture gx={18.6} gy={8.6} teinte="#16A34A" />
          <Voiture gx={6.9} gy={8.6} teinte="#FAFAFA" />
          <Voiture gx={8.6} gy={12.6} teinte="#DC2626" />
          <Voiture gx={9.2} gy={15.4} teinte="#F59E0B" />
        </g>

        {dessinables.map(({ quartier, plan }) => {
          const equipe = quartiersEquipes.includes(quartier.id);
          const actif = quartierActif === quartier.id;
          const { parcelle } = plan;

          return (
            <g
              key={quartier.id}
              role="button"
              tabIndex={0}
              aria-pressed={actif}
              aria-label={`${quartier.nom} — ${equipe ? T.carte.quartierAmeliore : T.carte.quartierAExplorer}`}
              className="cursor-pointer outline-none"
              onClick={() => onChoisir(quartier.id)}
              onKeyDown={(evenement) => {
                if (evenement.key === 'Enter' || evenement.key === ' ') {
                  evenement.preventDefault();
                  onChoisir(quartier.id);
                }
              }}
            >
              {/* La parcelle a une épaisseur : elle se lit comme un terrain, pas comme un décalque. */}
              <Bloc
                gx={parcelle.gx}
                gy={parcelle.gy}
                l={parcelle.l}
                p={parcelle.p}
                h={6}
                teinte={equipe ? plan.solEquipe : plan.sol}
                ombre={false}
              />
              <polygon
                points={losange(parcelle.gx, parcelle.gy, parcelle.l, parcelle.p, 6)}
                fill="none"
                stroke={actif ? '#0284C7' : 'transparent'}
                strokeWidth={actif ? 5 : 0}
                strokeLinejoin="round"
                className="transition-[stroke] duration-[250ms]"
              />

              <g transform="translate(0,-6)">
                {plan.decor(equipe)}
                {plan.lampes.map(([gx, gy]) => (
                  <Lampadaire key={`${quartier.id}-${gx}-${gy}`} gx={gx} gy={gy} allume={equipe} />
                ))}
              </g>
            </g>
          );
        })}

        {/*
          La rivière coule devant la ville, au-delà du socle : c'est la berge
          qui ferme la composition au premier plan. Deux ponts prolongent les
          avenues au-dessus de l'eau.
        */}
        <g aria-hidden="true">
          <polygon points={losange(-0.4, 18.2, 21.2, 1.8)} fill="#4FB4E4" />
          <polygon points={losange(-0.4, 18.5, 21.2, 1.1)} fill="#8AD6F5" />
          <polygon points={losange(8, 18.1, 2, 2)} fill="#BFC2CA" />
          <polygon points={losange(8, 18.1, 2, 0.22)} fill="#8E9299" />
          <polygon points={losange(13.6, 18.1, 0.9, 2)} fill="#BFC2CA" />
          <polygon points={losange(13.6, 18.1, 0.9, 0.22)} fill="#8E9299" />
        </g>

        {/* La berge, plantée, entre les quartiers et l'eau. */}
        <g aria-hidden="true">
          <Buisson gx={2.2} gy={17.6} />
          <Arbre gx={5.2} gy={17.5} taille={0.7} teinte="#4D7C0F" />
          <Buisson gx={11.4} gy={17.6} />
          <Arbre gx={16.2} gy={17.5} taille={0.75} />
          <Buisson gx={19.6} gy={17.6} />
        </g>

        {/*
          Les étiquettes passent en dernier, au-dessus de tous les volumes : en
          vue de trois quarts, un nom posé dans la couche de son quartier finit
          caché par l'immeuble du quartier d'à côté. Elles laissent passer le
          clic, qui atteint la parcelle placée dessous.
        */}
        <g aria-hidden="true" className="pointer-events-none">
          {dessinables.map(({ quartier, plan }) => {
            const ancre = proj(plan.etiquette.gx, plan.etiquette.gy, plan.etiquette.h);
            const largeur = quartier.nom.length * 10.6 + 30;
            return (
              <g key={`etiquette-${quartier.id}`}>
                <rect
                  x={ancre.x - largeur / 2}
                  y={ancre.y - 22}
                  width={largeur}
                  height="31"
                  rx="15"
                  fill="#FFFFFF"
                  opacity="0.94"
                />
                <text
                  x={ancre.x}
                  y={ancre.y}
                  textAnchor="middle"
                  className="fill-slate-900 text-[19px] font-bold"
                >
                  {quartier.nom}
                </text>
              </g>
            );
          })}
        </g>
      </svg>

      <figcaption className="mt-2 text-center text-sm text-slate-600">{T.carte.legende}</figcaption>
    </figure>
  );
}
